import React from 'react'
import PropTypes from 'prop-types'
import MaskedInput from 'react-text-mask'
import { Input, Checkbox, Alert, Select } from 'element-react'
import TemporarySelect from '../helpers/TemporarySelect'

// This is used by password fields and needs to be a standard function to
// support the use of `this` correctly. (not an arrow function)
export const togglePasswordVisibility = function(stateValue) {
    this.setState(oldState => ({ [stateValue]: !oldState[stateValue] }))
}

const basicFieldPropTypes = {
    input: PropTypes.object,
    meta: PropTypes.shape({
        error: PropTypes.string,
        touched: PropTypes.bool,
        warning: PropTypes.string,
    }),
}
const sharedFieldPropTypes = {
    ...basicFieldPropTypes,
    label: PropTypes.string,
    type: PropTypes.string,
    disabled: PropTypes.bool,
}

// Field level validation functions
export const Check = {
    filled: value => (value ? undefined : 'Required'),
    emailFormat: email =>
        /^.+@.+\..{1,63}$/i.test(email) ? undefined : 'Invalid email address',
    passwordLength: password =>
        password && password.length >= 6
            ? undefined
            : 'Password minimum is 6 characters',
    phoneFormat: number =>
        /^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/i.test(number)
            ? undefined
            : 'Phone number must be 10 digits',
}

// Form level validation functions
export const CheckForm = {
    passwordsMatch: (passwordFieldName, confirmationFieldName) => values =>
        values[passwordFieldName] === values[confirmationFieldName]
            ? {}
            : { [confirmationFieldName]: 'Must match password' },
}

/** An unlabeled input field with placeholder text like '_Name______".
 *
 *  Previously called renderField in other files.
 */
const UnlabeledInputField = ({
    input,
    label,
    disabled,
    meta: { touched, error, warning, submitFailed },
    ...custom
}) => (
    <div>
        <div className="input-container">
            <Input
                {...input}
                {...custom}
                id={input.name}
                type={input.type}
                disabled={disabled}
                placeholder={label}
            />
            {(touched || submitFailed) &&
                ((error && (
                    <p className="input--alert alert-color">
                        <i>{error}</i>
                    </p>
                )) ||
                    (warning && (
                        <p>
                            <i>{warning}</i>
                        </p>
                    )))}
        </div>
    </div>
)

UnlabeledInputField.propTypes = sharedFieldPropTypes
export { UnlabeledInputField }

/** A labeled input field like 'Name: __________".
 *
 *  Previously called renderField in this file.
 */
const LabeledInputField = ({
    input,
    label,
    disabled,
    placeholder,
    autoComplete,
    meta: { touched, error, warning, submitFailed },
    customOnChange,
    ...custom
}) => (
    <div>
        <label>{label}</label>
        <div className="input-container">
            <Input
                {...input}
                {...custom}
                id={input.name}
                type={input.type}
                disabled={disabled}
                onChange={newValue => {
                    input.onChange(newValue)
                    customOnChange ? customOnChange(newValue) : null
                }}
                placeholder={placeholder}
                autoComplete={autoComplete}
            />

            {(touched || submitFailed) &&
                ((error && (
                    <p className="input--alert alert-color">
                        <i>{error}</i>
                    </p>
                )) ||
                    (warning && (
                        <p>
                            <i>{warning}</i>
                        </p>
                    )))}
        </div>
    </div>
)

LabeledInputField.propTypes = {
    ...sharedFieldPropTypes,
    autoComplete: PropTypes.string,
    placeholder: PropTypes.string,
}
export { LabeledInputField }

const MaskedCountryId = ({
    input,
    label,
    meta: { touched, error, warning, submitFailed },
    ...custom
}) => (
    <div className="small-2 columns">
        <label>{label}</label>
        {/* This div and the class 'el-input__inner' below are hacks to
            get the look of the Input element from element-react without having
            to actually use it. */}
        <div className="el-input">
            <MaskedInput
                {...custom}
                autoComplete="off"
                className="el-input__inner"
                guide={false}
                mask={['+', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
                maxLength={7}
                onChange={input.onChange}
                value={input.value}
                placeholder="+1"
            />
        </div>

        {(touched || submitFailed) &&
            ((error && (
                <p className="input--alert alert-color">
                    <i>{error}</i>
                </p>
            )) ||
                (warning && (
                    <p>
                        <i>{warning}</i>
                    </p>
                )))}
    </div>
)

MaskedCountryId.propTypes = {
    ...sharedFieldPropTypes,
    autoComplete: PropTypes.string,
    mask: PropTypes.string,
}
export { MaskedCountryId }

const MaskedPhoneNumber = ({
    input,
    label,
    meta: { touched, error, warning, submitFailed },
    ...custom
}) => (
    <div className="small-10 columns">
        <label>{label}</label>
        <div className="input-container">
            {/* This div and the class 'el-input__inner' below are hacks to
            get the look of the Input element from element-react without having
            to actually use it. */}
            <div className="el-input">
                <MaskedInput
                    {...input}
                    {...custom}
                    autoComplete="tel"
                    className="el-input__inner"
                    guide={false}
                    mask={[
                        '(',
                        /[1-9]/,
                        /\d/,
                        /\d/,
                        ')',
                        ' ',
                        /\d/,
                        /\d/,
                        /\d/,
                        '-',
                        /\d/,
                        /\d/,
                        /\d/,
                        /\d/,
                    ]}
                    maxLength={14}
                    onChange={input.onChange}
                    placeholder="(123) 456-7890"
                    value={input.value}
                />
            </div>

            {(touched || submitFailed) &&
                ((error && (
                    <p className="input--alert alert-color">
                        <i>{error}</i>
                    </p>
                )) ||
                    (warning && (
                        <p>
                            <i>{warning}</i>
                        </p>
                    )))}
        </div>
    </div>
)

MaskedPhoneNumber.propTypes = {
    ...sharedFieldPropTypes,
    autoComplete: PropTypes.string,
    mask: PropTypes.string,
    placeholder: PropTypes.string,
}
export { MaskedPhoneNumber }

/** A styled checkbox component.
 *
 * Previously called 'renderCheckbox' in some files.
 */
const CustomCheckbox = ({
    className,
    input,
    label,
    meta: { touched, error },
}) => (
    <ul className={`no-bullet${className ? ` ${className}` : ''}`}>
        <li>
            <Checkbox
                name={input.name}
                onChange={value => {
                    input.onChange(value)
                }}
                checked={input.checked}
            >
                {label}
            </Checkbox>
        </li>
        {touched && error && (
            <Alert title={error} type="error" closable={false} />
        )}
    </ul>
)

CustomCheckbox.propTypes = {
    ...basicFieldPropTypes,
    className: PropTypes.string,
    label: PropTypes.string,
    checked: PropTypes.bool,
}
export { CustomCheckbox }

const LocationCheckboxGroup = ({
    input,
    meta: { touched, error },
    options,
}) => (
    <div className="form-group">
        <Checkbox.Group
            value={input.value.slice()}
            name={input.name}
            onChange={values => input.onChange(values.slice())}
            className="vertical descriptions"
        >
            {options.map(option => (
                <Checkbox key={'key' + option.id} label={'' + option.id}>
                    {option.label}
                    <span className="description">{option.address}</span>
                </Checkbox>
            ))}
        </Checkbox.Group>
        {touched && error && (
            <Alert title={error} type="error" closable={false} />
        )}
    </div>
)

LocationCheckboxGroup.propTypes = {
    ...basicFieldPropTypes,
    options: PropTypes.array,
}
export { LocationCheckboxGroup }

const CustomSelect = ({
    input,
    label,
    options,
    placeholder,
    meta: { touched, error, warning, submitFailed },
}) => (
    <div>
        <label>{label}</label>
        <div className="input-container">
            <Select {...input} name={input.name} placeholder={placeholder}>
                {options}
            </Select>
            {(touched || submitFailed) &&
                ((error && (
                    <p className="input--alert alert-color">
                        <i>{error}</i>
                    </p>
                )) ||
                    (warning && (
                        <p>
                            <i>{warning}</i>
                        </p>
                    )))}
        </div>
    </div>
)

CustomSelect.propTypes = {
    ...basicFieldPropTypes,
    label: PropTypes.string,
    options: PropTypes.array,
    placeholder: PropTypes.string,
}
export { CustomSelect }

/** Temporary component until react-select's Select works on IE11 */
const TempCustomSelect = ({
    input,
    label,
    options,
    placeholder,
    meta: { touched, error, warning, submitFailed },
}) => (
    <div>
        <label>{label}</label>
        <div className="input-container">
            <TemporarySelect
                {...input}
                name={input.name}
                placeholder={placeholder}
            >
                {options}
            </TemporarySelect>
            {(touched || submitFailed) &&
                ((error && (
                    <p className="input--alert alert-color">
                        <i>{error}</i>
                    </p>
                )) ||
                    (warning && (
                        <p>
                            <i>{warning}</i>
                        </p>
                    )))}
        </div>
    </div>
)

TempCustomSelect.propTypes = {
    ...basicFieldPropTypes,
    label: PropTypes.string,
    options: PropTypes.array,
    placeholder: PropTypes.string,
}
export { TempCustomSelect }
